/* eslint-disable max-lines-per-function */
/* eslint-disable functional/immutable-data */
/* eslint-disable functional/prefer-readonly-type */

import React, { ChangeEvent } from 'react'
import { authFetch } from '~/services/fetch'
import {
  Grid,
  List,
  Card,
  CardHeader,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  Button,
  Divider,
  Paper,
  Typography,
  TextField,
  FormControlLabel,
} from '@mui/material'
import { useLang } from '~/hooks/useLang'
import {
  UseFormSetValue,
  UseFormGetValues,
  UseFormRegister,
} from 'react-hook-form'
import { State, useHookstate } from '@hookstate/core'
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view'

interface IListProps {
  id: number
  name: string
  [key: string]: unknown
}

interface CrudListTransferFieldProps<T extends Record<string, unknown>> {
  readonly model: string
  readonly setFormValue: UseFormSetValue<T>
  readonly getValues?: UseFormGetValues<T>
  readonly register?: UseFormRegister<T>
  readonly placeholder?: string
  readonly modelPlaceholder?: string
  readonly showColumnText?: string
  readonly options: {
    firstListUrl: string
    secondListUrl: string
  }
  readonly isNoOperation?: boolean
  readonly enableGrouping?: boolean
  readonly groupByField?: string
}

export default function CrudListTransferField<T extends Record<string, unknown>>({
  model,
  options,
  modelPlaceholder,
  placeholder,
  setFormValue,
  showColumnText = 'name',
  isNoOperation,
  enableGrouping = false,
  groupByField = 'agency',
}: CrudListTransferFieldProps<T>) {
  const { translate } = useLang()
  const displayPlaceholder = modelPlaceholder || placeholder || 'Items'

  const [checked, setChecked] = React.useState<Array<IListProps | null>>([])
  const [left, setLeft] = React.useState<Array<IListProps | null>>([])
  const [right, setRight] = React.useState<Array<IListProps | null>>([])
  const filterLeft = useHookstate<string>('')
  const filterRight = useHookstate<string>('')

  React.useEffect(() => {
    const fetchRight = async () => {
      if (!options.firstListUrl) return []
      const response = await authFetch({ url: options.firstListUrl, method: 'GET' })
      return response.data as Array<IListProps>
    }

    fetchRight()
      .then((rightData) => {
        const fetchLeft = async () => {
          const response = await authFetch({ url: options.secondListUrl, method: 'GET' })
          const data = (response.data as Array<IListProps>).filter((item) =>
            isNoOperation ? !Boolean((item as IListProps).is_non_operational) : true
          )
          setLeft(not(data, rightData))
          setRight(rightData)
          // setFormValue usando casting direto sem tipos complexos
          setFormValue(
            model as any,
            rightData.map((element) => element.id) as any
          )
        }
        fetchLeft().catch(console.error)
      })
      .catch(console.error)
  }, [])

  const rightChecked = intersection(checked, right)
  const leftChecked = intersection(checked, left)

  const isChecked = (value: IListProps | null) => !!checked.find((c) => c?.id === value?.id)

  function not(firstList: Array<IListProps | null>, secondList: Array<IListProps | null>) {
    const secondIds = new Set(secondList.map((v) => v?.id))
    return firstList.filter((value) => !secondIds.has(value?.id))
  }

  function intersection(firstList: Array<IListProps | null>, secondList: Array<IListProps | null>) {
    const secondIds = new Set(secondList.map((v) => v?.id))
    return firstList.filter((value) => secondIds.has(value?.id))
  }

  function union(firstList: Array<IListProps | null>, secondList: Array<IListProps | null>) {
    return [...firstList, ...not(secondList, firstList)]
  }

  const handleToggle = (value: IListProps | null) => () => {
    const newChecked = [...checked]
    if (!isChecked(value)) {
      newChecked.push(value)
      return setChecked(newChecked)
    }
    return setChecked(newChecked.filter((item) => item?.id !== value?.id))
  }

  const optionOfChecked = (items: Array<IListProps | null>) => intersection(checked, items).length

  const handleToggleAll =
    (items: Array<IListProps | null>) =>
    () => {
      if (optionOfChecked(items) === items.length) {
        setChecked(not(checked, items))
      } else {
        setChecked(union(checked, items))
      }
    }

  const handleCheckedRight = () => {
    const newRight = right.concat(leftChecked)
    setRight(newRight)
    setFormValue(model as any, newRight.map((element) => element?.id) as any)
    setLeft(not(left, leftChecked))
    setChecked(not(checked, leftChecked))
  }

  const handleCheckedLeft = () => {
    const newRight = not(right, rightChecked)
    setLeft(left.concat(rightChecked))
    setRight(newRight)
    setFormValue(model as any, newRight.map((element) => element?.id) as any)
    setChecked(not(checked, rightChecked))
  }

  const groupItems = (items: Array<IListProps | null>) => {
    if (!enableGrouping || !groupByField) return { ungrouped: items }

    const groups = items.reduce((acc: Record<string, Array<IListProps | null>>, item) => {
      if (!item) return acc
      const groupField = item[groupByField] as Record<string, unknown> | undefined
      const groupKey = (groupField?.name as string) || 'Sem Grupo'
      if (!acc[groupKey]) acc[groupKey] = []
      acc[groupKey].push(item)
      return acc
    }, {})

    if (Object.keys(groups).length <= 1) return { ungrouped: items }
    return groups
  }

  const customList = (title: React.ReactNode, items: Array<IListProps | null>, filter: State<string>) => {
    const filterValue = filter.get() || ''
    const filteredItems = filterValue
      ? items.filter((item) => {
          if (!item) return false
          const fieldValue = String(item[showColumnText] ?? '')
          return fieldValue.toUpperCase().includes(filterValue.toUpperCase())
        })
      : items

    const groupedItems = groupItems(filteredItems)

    return (
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardHeader
          avatar={
            <Checkbox
              onClick={handleToggleAll(items)}
              checked={optionOfChecked(items) === items.length && items.length !== 0}
              indeterminate={optionOfChecked(items) !== items.length && optionOfChecked(items) !== 0}
              disabled={items.length === 0}
            />
          }
          title={title}
          subheader={`${optionOfChecked(items)}/${items.length} ${translate('Selected')}`}
        />
        <Divider />
        <TextField
          fullWidth
          label={`${translate('Search')} ${translate(displayPlaceholder)}`}
          size="small"
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            filter.set(event.target.value)
          }}
        />
        <List
          sx={{
            width: '100%',
            flex: 1,
            minHeight: 0,
            backgroundColor: 'background.paper',
            overflow: 'auto',
          }}
          dense
          component="div"
          role="list"
        >
          {enableGrouping && !groupedItems.ungrouped ? (
            <SimpleTreeView multiSelect={false} sx={{ width: '100%' }}>
              {Object.entries(groupedItems).map(([groupName, groupItemsArr]) => {
                const groupCount = optionOfChecked(groupItemsArr)
                const groupAll = groupCount === groupItemsArr.length && groupItemsArr.length !== 0
                const groupIndeterminate = groupCount > 0 && groupCount < groupItemsArr.length

                const handleToggleGroup = () => {
                  if (groupAll) {
                    setChecked(not(checked, groupItemsArr))
                  } else {
                    setChecked(union(checked, groupItemsArr))
                  }
                }

                return (
                  <TreeItem
                    key={`group-${groupName}`}
                    itemId={`group-${groupName}`}
                    label={
                      <FormControlLabel
                        onClick={(e) => e.stopPropagation()}
                        control={
                          <Checkbox
                            checked={groupAll}
                            indeterminate={groupIndeterminate}
                            onClick={(e) => {
                              e.stopPropagation()
                              handleToggleGroup()
                            }}
                            tabIndex={-1}
                            disableRipple
                            inputProps={{ 'aria-label': `${groupName} group toggle` }}
                          />
                        }
                        label={`${groupName} (${groupItemsArr.length})`}
                      />
                    }
                  >
                    {groupItemsArr.map((element) => {
                      const labelId = `transfer-list-all-item-${element?.id}-label`
                      return (
                        <TreeItem
                          key={`item-${element?.id}`}
                          itemId={`item-${element?.id}`}
                          label={
                            <div
                              style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Checkbox
                                checked={isChecked(element)}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleToggle(element)()
                                }}
                                tabIndex={-1}
                                disableRipple
                                inputProps={{ 'aria-labelledby': labelId }}
                              />
                              <span id={labelId}>
{element && String(element[showColumnText] ?? '')}
</span>
                            </div>
                          }
                        />
                      )
                    })}
                  </TreeItem>
                )
              })}
            </SimpleTreeView>
          ) : (
            (groupedItems.ungrouped || filteredItems).map((element) => {
              const labelId = `transfer-list-all-item-${element?.id}-label`
              return (
                <ListItem key={element?.id} role="listitem" button onClick={handleToggle(element)}>
                  <ListItemIcon>
                    <Checkbox checked={isChecked(element)} tabIndex={-1} disableRipple inputProps={{ 'aria-labelledby': labelId }} />
                  </ListItemIcon>
                  <ListItemText id={labelId} primary={`${element && String(element[showColumnText] ?? '')}`} />
                </ListItem>
              )
            })
          )}
          <ListItem />
        </List>
      </Card>
    )
  }

  return (
    <Grid item xs={12} sx={{ height: '70vh' }}>
      <Paper variant="outlined" sx={{ height: '100%', width: '100%' }}>
        <Typography sx={{ textAlign: 'center', paddingY: 2 }}>
{translate(displayPlaceholder)}
</Typography>
        <Grid container item xs={12} alignItems="stretch" sx={{ justifyContent: 'center', height: '100%', minHeight: 0 }}>
          <Grid item xs={5} sx={{ height: '90%' }}>
            {customList(
              translate('Choices'),
              filterLeft.get()
                ? left.filter((item) => {
                    if (!item) return false
                    const fieldValue = String(item[showColumnText] ?? '')
                    return fieldValue.toUpperCase().includes(filterLeft.get().toUpperCase())
                  })
                : left,
              filterLeft
            )}
          </Grid>
          <Grid item sx={{ height: '90%', minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Grid container direction="column" alignItems="center" justifyContent={'center'} sx={{ height: '100%' }}>
              <Button sx={{ margin: 0.5 }} variant="outlined" size="small" onClick={handleCheckedRight} disabled={leftChecked.length === 0}>
                &gt;
              </Button>
              <Button sx={{ margin: 0.5 }} variant="outlined" size="small" onClick={handleCheckedLeft} disabled={rightChecked.length === 0}>
                &lt;
              </Button>
            </Grid>
          </Grid>
          <Grid item xs={5} sx={{ height: '90%', minHeight: 0 }}>
            {customList(
              translate('Chosen'),
              filterRight.get()
                ? right.filter((item) => {
                    if (!item) return false
                    const fieldValue = String(item[showColumnText] ?? '')
                    return fieldValue.toUpperCase().includes(filterRight.get().toUpperCase())
                  })
                : right,
              filterRight
            )}
          </Grid>
        </Grid>
      </Paper>
    </Grid>
  )
}
